<script setup lang="ts">
import { computed } from 'vue'
import { useLocale } from '@render/composables/useLocale'
import {
  buildLocaleConfig,
  formatCurrencyShort,
  formatCurrencyLong,
} from '@render/lib/localeConfig'

const props = defineProps<{
  amount: number
  variant?: 'default' | 'narrow' | 'code'
  /** The 3-letter currency code (e.g., 'usd', 'eur') — overrides the pipe currency. */
  currency?: string
  /** The 2-letter language code (e.g., 'en', 'de') — overrides the pipe language. */
  language?: string
}>()

const loc = useLocale()

// Config-driven: separators, spacing, symbol and code all come from the
// effective LocaleConfig (derived from locale + per-(lang,geo) override).
// When an explicit currency/language prop is passed (legacy), build a config
// for it; the per-currency symbol/code override is dropped since it was
// resolved for the pipe's own currency, not this one.
const effectiveConfig = computed(() => {
  if (props.currency || props.language) {
    const ovr = loc.override.value
      ? { ...loc.override.value, currencySymbol: undefined, currencyCode: undefined }
      : undefined
    return buildLocaleConfig(
      props.language || loc.language.value,
      loc.geography.value,
      props.currency || loc.currency.value,
      ovr,
    )
  }
  return loc.config.value
})

const formattedCurrency = computed(() => {
  try {
    return props.variant === 'code'
      ? formatCurrencyLong(props.amount, effectiveConfig.value)
      : formatCurrencyShort(props.amount, effectiveConfig.value)
  } catch {
    const code = (props.currency || loc.currency.value).toUpperCase()
    console.warn(`[CurrencyFormatter] Failed to format ${props.amount} / ${code}`)
    return `${props.amount} ${code}`
  }
})
</script>

<template>
  <span class="currency"> {{ formattedCurrency }} </span>
</template>
